<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WhatsappInteractiveMessageTemplatesAPI from 'dashboard/api/whatsappInteractiveMessageTemplates';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CurrencyInput from 'dashboard/components-next/input/CurrencyInput.vue';
import { validPixCode, validPixAmount } from './pixPayment';
import AsaasPixChargeCard from './AsaasPixChargeCard.vue';
import SavedPixKeyCard from './SavedPixKeyCard.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';
import AiTemplateDraft from 'dashboard/components-next/whatsapp/AiTemplateDraft.vue';
import { existingTemplateDraft } from 'dashboard/helper/aiTemplateDraft';
import { insertAtCursor } from 'dashboard/helper/systemParameters';
import SystemParameterMenu from 'dashboard/components-next/systemParameters/SystemParameterMenu.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  purpose: {
    type: String,
    default: 'send',
    validator: value => ['send', 'template'].includes(value),
  },
  inboxId: { type: [Number, String], default: null },
  // display_id da conversa: habilita gerar a cobrança Pix no Asaas da empresa.
  conversationId: { type: [Number, String], default: null },
});

const emit = defineEmits(['send', 'save', 'close']);
const { t } = useI18n();
const dialogRef = ref(null);
const activeSection = ref('setup');
const editorBody = ref(null);
const pixDeliveryMode = ref('native');
const changeSection = async section => {
  activeSection.value = section;
  await nextTick();
  if (editorBody.value?.parentElement)
    editorBody.value.parentElement.scrollTop = 0;
};
const aiGenerationSession = ref(0);
// A configuração da ação fica na mesma etapa do seletor de tipo: ao escolher
// uma modalidade, os campos aparecem imediatamente. WhatsApp Flow não é mais
// oferecido para novas mensagens (Flows são enviados pela etapa do Funil);
// mensagens e modelos legados do tipo Flow continuam editáveis e enviáveis.
const editorSections = computed(() => [
  { key: 'setup', label: t('CONVERSATION.INTERACTIVE.ACTION_TITLE') },
  { key: 'content', label: t('CONVERSATION.INTERACTIVE.CONTENT_TITLE') },
]);
const templateName = ref('');
const editingTemplateId = ref(null);
const selectedTemplateId = ref('');
const savedTemplates = ref([]);
const isLoadingTemplates = ref(false);
const pixSource = ref('');
const savedPixReceiver = ref('');

const defaultForm = () => ({
  type: 'button',
  referenceId: '',
  pixAmount: '',
  pixDescription: '',
  pixCode: '',
  pixUrl: '',
  merchantName: '',
  pixKey: '',
  pixKeyType: 'CNPJ',
  body: '',
  headerType: '',
  headerText: '',
  headerMediaUrl: '',
  headerFilename: '',
  footerText: '',
  buttons: [{ title: '' }],
  ctaDisplayText: '',
  ctaUrl: '',
  voiceCallText: 'Ligar agora',
  voiceCallTtlMinutes: '',
  flowId: '',
  flowToken: '',
  flowCta: '',
  flowAction: 'navigate',
  flowScreen: '',
  flowData: '',
  flowMode: 'published',
  catalogThumbnailProductRetailerId: '',
});

const form = reactive(defaultForm());

// Parâmetros do sistema (nome do contato, atendente...) entram no cursor do campo e
// são substituídos pelo servidor no envio, no corpo, cabeçalho e rodapé.
const headerTextField = ref(null);
const bodyField = ref(null);
const footerField = ref(null);
const insertParameter = async (field, token, wrapper) => {
  const element = wrapper?.querySelector('textarea, input');
  const { value, caret } = insertAtCursor(form[field], token, element);
  form[field] = value;
  await nextTick();
  element?.focus();
  element?.setSelectionRange?.(caret, caret);
};
const isTemplatePurpose = computed(() => props.purpose === 'template');

const dialogTitle = computed(() => {
  if (!isTemplatePurpose.value) return t('CONVERSATION.INTERACTIVE.TITLE');
  return editingTemplateId.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.EDIT_TITLE')
    : t('CONVERSATION.INTERACTIVE.TEMPLATES.NEW_TITLE');
});

const dialogDescription = computed(() =>
  isTemplatePurpose.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.FORM_DESCRIPTION')
    : t('CONVERSATION.INTERACTIVE.DESCRIPTION')
);

const confirmLabel = computed(() =>
  isTemplatePurpose.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.SAVE')
    : t('CONVERSATION.INTERACTIVE.SEND')
);

const savedTemplateOptions = computed(() => [
  {
    value: '',
    label: t('CONVERSATION.INTERACTIVE.TEMPLATES.EMPTY_SELECTION'),
  },
  ...savedTemplates.value.map(template => ({
    value: String(template.id),
    label: template.name,
  })),
]);

const TYPE_ICONS = {
  order_details: 'i-hugeicons-qr-code',
  button: 'i-lucide-reply',
  cta_url: 'i-lucide-external-link',
  voice_call: 'i-simple-icons-whatsapp',
  location_request_message: 'i-lucide-map-pin',
  flow: 'i-lucide-clipboard-list',
  catalog_message: 'i-lucide-shopping-bag',
};
const TYPE_HINT_KEYS = {
  order_details: 'ORDER_DETAILS',
  button: 'BUTTON',
  cta_url: 'CTA_URL',
  voice_call: 'VOICE_CALL',
  location_request_message: 'LOCATION',
  flow: 'FLOW',
  catalog_message: 'CATALOG',
};
const baseTypeOptions = computed(() => [
  { value: 'order_details', label: t('CONVERSATION.INTERACTIVE.PIX.TITLE') },
  { value: 'button', label: t('CONVERSATION.INTERACTIVE.TYPES.BUTTON') },
  { value: 'cta_url', label: t('CONVERSATION.INTERACTIVE.TYPES.CTA_URL') },
  {
    value: 'voice_call',
    label: t('CONVERSATION.INTERACTIVE.TYPES.VOICE_CALL'),
  },
  {
    value: 'location_request_message',
    label: t('CONVERSATION.INTERACTIVE.TYPES.LOCATION'),
  },
  ...(form.type === 'flow'
    ? [{ value: 'flow', label: t('CONVERSATION.INTERACTIVE.TYPES.FLOW') }]
    : []),
  {
    value: 'catalog_message',
    label: t('CONVERSATION.INTERACTIVE.TYPES.CATALOG'),
  },
]);
const typeOptions = computed(() =>
  baseTypeOptions.value.map(option => ({
    ...option,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    hint: t(
      `CONVERSATION.INTERACTIVE.TYPES.HINTS.${TYPE_HINT_KEYS[option.value]}`
    ),
  }))
);

const headerOptions = computed(() => [
  { value: '', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.NONE') },
  { value: 'text', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.TEXT') },
  { value: 'image', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.IMAGE') },
  { value: 'video', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.VIDEO') },
  {
    value: 'document',
    label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.DOCUMENT'),
  },
]);

const flowActionOptions = computed(() => [
  { value: 'navigate', label: t('CONVERSATION.INTERACTIVE.FLOW.NAVIGATE') },
  {
    value: 'data_exchange',
    label: t('CONVERSATION.INTERACTIVE.FLOW.DATA_EXCHANGE'),
  },
]);

const whatsappFlows = ref([]);
const isLoadingFlows = ref(false);
const flowsLoaded = ref(false);

const flowModeOptions = computed(() => [
  {
    value: 'published',
    label: t('CONVERSATION.INTERACTIVE.FLOW.MODE_PUBLISHED'),
  },
  { value: 'draft', label: t('CONVERSATION.INTERACTIVE.FLOW.MODE_DRAFT') },
]);

const selectableFlows = computed(() =>
  whatsappFlows.value.filter(flow =>
    ['PUBLISHED', 'DRAFT'].includes(flow.status)
  )
);

const flowOptions = computed(() =>
  selectableFlows.value.map(flow => ({
    value: flow.meta_flow_id,
    label: [
      flow.name,
      props.inboxId ? null : flow.inbox_name,
      t(`WHATSAPP_FLOWS.STATUS.${flow.status}`),
    ]
      .filter(Boolean)
      .join(' · '),
  }))
);

const selectedFlow = computed(() =>
  whatsappFlows.value.find(flow => flow.meta_flow_id === form.flowId)
);

const flowOutsideInbox = computed(
  () =>
    !!props.inboxId &&
    flowsLoaded.value &&
    !!form.flowId.trim() &&
    !selectedFlow.value
);

const generateFlowToken = () =>
  `talkvia_${crypto.randomUUID().replaceAll('-', '')}`;

const loadWhatsappFlows = async () => {
  isLoadingFlows.value = true;
  try {
    const params = props.inboxId ? { inbox_id: props.inboxId } : {};
    const { data } = await WhatsappFlowsAPI.list(params);
    whatsappFlows.value = data.payload || [];
  } catch {
    whatsappFlows.value = [];
  } finally {
    isLoadingFlows.value = false;
    flowsLoaded.value = true;
  }
};

const selectFlow = metaFlowId => {
  form.flowId = String(metaFlowId || '');
  const flow = selectedFlow.value;
  if (!flow) return;
  form.flowAction = 'navigate';
  form.flowScreen = flow.first_screen_id || form.flowScreen;
  form.flowMode = flow.status === 'DRAFT' ? 'draft' : 'published';
  if (!form.flowToken.trim()) form.flowToken = generateFlowToken();
};

const createFlowUrl = computed(() => {
  const accountId = window.location.pathname.split('/')[3];
  const query = { new: '1' };
  if (props.inboxId) query.inbox_id = props.inboxId;
  return frontendURL(`accounts/${accountId}/campanhas/whatsapp-flows`, query);
});

const openFlowCreation = () => {
  window.open(createFlowUrl.value, '_blank', 'noopener,noreferrer');
};

watch(
  () => form.type,
  type => {
    if (type !== 'flow') return;
    if (!form.flowToken.trim()) form.flowToken = generateFlowToken();
    if (!flowsLoaded.value && !isLoadingFlows.value) loadWhatsappFlows();
  }
);

const flowDataError = computed(() => {
  if (!form.flowData.trim()) return '';
  try {
    const value = JSON.parse(form.flowData);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? ''
      : t('CONVERSATION.INTERACTIVE.ERRORS.FLOW_DATA');
  } catch {
    return t('CONVERSATION.INTERACTIVE.ERRORS.FLOW_DATA');
  }
});

const hasValidHeader = computed(() => {
  if (!form.headerType) return true;
  if (form.headerType === 'text') return !!form.headerText.trim();
  return /^https?:\/\//i.test(form.headerMediaUrl.trim());
});

const pixKeyOptions = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'].map(value => ({
  value,
  label: value === 'EVP' ? 'Chave aleatória' : value,
}));
watch(
  () => form.type,
  type => {
    if (type === 'order_details' && !form.referenceId)
      form.referenceId = crypto.randomUUID().replaceAll('-', '');
  }
);
const hasValidAction = computed(() => {
  if (form.type === 'order_details') {
    if (!isTemplatePurpose.value && pixDeliveryMode.value !== 'native')
      return false;
    const receiverValid =
      !!form.pixDescription.trim() &&
      !!form.merchantName.trim() &&
      !!form.pixKey.trim();
    return (
      receiverValid &&
      (isTemplatePurpose.value ||
        (validPixAmount(form.pixAmount) &&
          validPixCode(form.pixCode.trim()) &&
          /^[a-zA-Z0-9_-]{1,35}$/.test(form.referenceId)))
    );
  }
  if (form.type === 'button') {
    const buttonTitles = form.buttons.map(button => button.title.trim());
    return (
      form.buttons.length >= 1 &&
      form.buttons.length <= 3 &&
      buttonTitles.every(Boolean) &&
      new Set(buttonTitles).size === buttonTitles.length
    );
  }
  if (form.type === 'cta_url') {
    return (
      !!form.ctaDisplayText.trim() && /^https?:\/\//i.test(form.ctaUrl.trim())
    );
  }
  if (form.type === 'voice_call') {
    const ttl = String(form.voiceCallTtlMinutes || '').trim();
    return (
      !!form.voiceCallText.trim() &&
      form.voiceCallText.trim().length <= 20 &&
      (!ttl || (/^\d+$/.test(ttl) && Number(ttl) >= 1 && Number(ttl) <= 43200))
    );
  }
  if (form.type === 'flow') {
    return (
      !!form.flowId.trim() &&
      !!form.flowToken.trim() &&
      !!form.flowCta.trim() &&
      (form.flowAction !== 'navigate' || !!form.flowScreen.trim()) &&
      !flowDataError.value
    );
  }
  return true;
});

const isValid = computed(
  () =>
    (!isTemplatePurpose.value || !!templateName.value.trim()) &&
    !!form.body.trim() &&
    form.body.length <= 1024 &&
    form.footerText.length <= 60 &&
    hasValidHeader.value &&
    hasValidAction.value
);

const actionLabel = computed(() => {
  if (form.type === 'order_details')
    return t('CONVERSATION.INTERACTIVE.PIX.COPY');
  if (form.type === 'cta_url') return form.ctaDisplayText;
  if (form.type === 'voice_call') return form.voiceCallText;
  if (form.type === 'location_request_message') {
    return t('CONVERSATION.INTERACTIVE.PREVIEW.SEND_LOCATION');
  }
  if (form.type === 'flow') return form.flowCta;
  if (form.type === 'catalog_message') {
    return t('CONVERSATION.INTERACTIVE.PREVIEW.VIEW_CATALOG');
  }
  return '';
});

const reset = () => {
  pixDeliveryMode.value = 'native';
  pixSource.value = '';
  aiGenerationSession.value += 1;
  Object.assign(form, defaultForm());
  templateName.value = '';
  editingTemplateId.value = null;
  selectedTemplateId.value = '';
};

const applyTemplate = template => {
  if (!template) return;

  const interactive = template.interactive || {};
  templateName.value = template.name || '';
  editingTemplateId.value = template.id || null;
  Object.assign(form, {
    ...defaultForm(),
    type: interactive.type || 'button',
    referenceId: interactive.reference_id || '',
    pixAmount: interactive.amount || '',
    pixDescription: interactive.description || '',
    pixCode: interactive.pix_code || '',
    pixUrl: interactive.pix_url || '',
    merchantName: interactive.merchant_name || '',
    pixKey: interactive.pix_key || '',
    pixKeyType: interactive.pix_key_type || 'CNPJ',
    body: template.content || '',
    headerType: interactive.header_type || '',
    headerText: interactive.header_text || '',
    headerMediaUrl: interactive.header_media_url || '',
    headerFilename: interactive.header_filename || '',
    footerText: interactive.footer_text || '',
    buttons:
      interactive.type === 'button'
        ? (interactive.buttons || []).map(button => ({
            title: button.title || button.id || '',
          }))
        : defaultForm().buttons,
    ctaDisplayText: interactive.cta_display_text || '',
    ctaUrl: interactive.cta_url || '',
    voiceCallText: interactive.voice_call_text || 'Ligar agora',
    voiceCallTtlMinutes: interactive.voice_call_ttl_minutes
      ? String(interactive.voice_call_ttl_minutes)
      : '',
    flowId: interactive.flow_id || '',
    flowToken: interactive.flow_token || '',
    flowCta: interactive.flow_cta || '',
    flowAction: interactive.flow_action || 'navigate',
    flowScreen: interactive.flow_screen || '',
    flowData: interactive.flow_data
      ? JSON.stringify(interactive.flow_data, null, 2)
      : '',
    flowMode: interactive.flow_mode === 'draft' ? 'draft' : 'published',
    catalogThumbnailProductRetailerId:
      interactive.catalog_thumbnail_product_retailer_id || '',
  });
};

const aiExistingDraft = computed(() =>
  existingTemplateDraft({
    name: templateName.value,
    body: form.body,
    header: form.headerType === 'text' ? form.headerText : '',
    footer: form.footerText,
    button_texts:
      form.type === 'button' ? form.buttons.map(button => button.title) : [],
    action_text:
      { cta_url: form.ctaDisplayText, flow: form.flowCta }[form.type] || '',
  })
);

const generateAiDraft = options =>
  WhatsappInteractiveMessageTemplatesAPI.generateTemplate(options);
const applyAiDraft = draft => {
  templateName.value = draft.name;
  form.body = draft.body;
  form.footerText = draft.footer;
  if (form.headerType === 'text') form.headerText = draft.header;
  if (form.type === 'button')
    form.buttons = draft.button_texts.map(title => ({ title }));
  if (form.type === 'cta_url' && draft.action_text)
    form.ctaDisplayText = draft.action_text;
  if (form.type === 'flow' && draft.action_text)
    form.flowCta = draft.action_text;
  activeSection.value = 'content';
};

const fetchSavedTemplates = async () => {
  if (isTemplatePurpose.value) return;

  isLoadingTemplates.value = true;
  try {
    const { data } = await WhatsappInteractiveMessageTemplatesAPI.get();
    savedTemplates.value = data;
  } catch {
    savedTemplates.value = [];
  } finally {
    isLoadingTemplates.value = false;
  }
};

watch(selectedTemplateId, value => {
  if (!value) return;
  const template = savedTemplates.value.find(
    item => String(item.id) === String(value)
  );
  applyTemplate(template);
  editingTemplateId.value = null;
});

// Cobrança criada no Asaas preenche os dados do Pix; o envio continua pelo botão da mensagem.
const applyAsaasCharge = charge => {
  pixDeliveryMode.value = charge.delivery_mode || 'native';
  pixSource.value = 'asaas';
  Object.assign(form, {
    pixAmount: charge.amount,
    pixDescription: charge.description,
    referenceId: charge.reference_id,
    pixCode: charge.pix_code,
    pixUrl: charge.pix_url || '',
    merchantName: charge.merchant_name,
    pixKey: charge.pix_key,
    pixKeyType: charge.pix_key_type,
  });
};

const applySavedKeyPayment = payment => {
  applyAsaasCharge(payment);
  pixSource.value = 'saved_key';
  savedPixReceiver.value = JSON.stringify([
    form.merchantName,
    form.pixKey,
    form.pixKeyType,
  ]);
  if (!form.body.trim()) form.body = t('PIX_KEYS.PAYMENT.DEFAULT_BODY');
};

const invalidateSavedKeyPayment = () => {
  if (pixSource.value === 'saved_key') form.pixCode = '';
};

watch(
  () => JSON.stringify([form.merchantName, form.pixKey, form.pixKeyType]),
  receiver => {
    if (receiver !== savedPixReceiver.value) invalidateSavedKeyPayment();
  }
);

const open = template => {
  reset();
  flowsLoaded.value = false;
  whatsappFlows.value = [];
  activeSection.value = template ? 'content' : 'setup';
  if (template) applyTemplate(template);
  if (form.type === 'flow') loadWhatsappFlows();
  fetchSavedTemplates();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleClose = () => emit('close');

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ title: '' });
};

const removeButton = index => {
  if (form.buttons.length === 1) return;
  form.buttons.splice(index, 1);
};

const buildInteractiveAttributes = () => {
  const interactive = {
    type: form.type,
    header_type: form.headerType || undefined,
    header_text:
      form.headerType === 'text' ? form.headerText.trim() : undefined,
    header_media_url:
      form.headerType && form.headerType !== 'text'
        ? form.headerMediaUrl.trim()
        : undefined,
    header_filename:
      form.headerType === 'document'
        ? form.headerFilename.trim() || undefined
        : undefined,
    footer_text: form.footerText.trim() || undefined,
  };

  if (form.type === 'order_details') {
    Object.assign(interactive, {
      ...(!isTemplatePurpose.value
        ? {
            reference_id: form.referenceId,
            amount: form.pixAmount,
            pix_code: form.pixCode.trim(),
            pix_url: form.pixUrl.trim() || undefined,
          }
        : {}),
      description: form.pixDescription.trim(),
      merchant_name: form.merchantName.trim(),
      pix_key: form.pixKey.trim(),
      pix_key_type: form.pixKeyType,
    });
  } else if (form.type === 'button') {
    interactive.buttons = form.buttons.map(button => ({
      id: button.title.trim(),
      title: button.title.trim(),
    }));
  } else if (form.type === 'cta_url') {
    interactive.cta_display_text = form.ctaDisplayText.trim();
    interactive.cta_url = form.ctaUrl.trim();
  } else if (form.type === 'voice_call') {
    interactive.voice_call_text = form.voiceCallText.trim();
    if (String(form.voiceCallTtlMinutes || '').trim())
      interactive.voice_call_ttl_minutes = Number(form.voiceCallTtlMinutes);
  } else if (form.type === 'flow') {
    interactive.flow_id = form.flowId.trim();
    interactive.flow_token = form.flowToken.trim();
    interactive.flow_cta = form.flowCta.trim();
    interactive.flow_action = form.flowAction;
    interactive.flow_screen = form.flowScreen.trim() || undefined;
    interactive.flow_data = form.flowData.trim()
      ? JSON.parse(form.flowData)
      : undefined;
    interactive.flow_mode = form.flowMode === 'draft' ? 'draft' : undefined;
  } else if (form.type === 'catalog_message') {
    interactive.catalog_thumbnail_product_retailer_id =
      form.catalogThumbnailProductRetailerId.trim() || undefined;
  }

  return interactive;
};

const handleConfirm = () => {
  if (props.isLoading) return;
  if (activeSection.value === 'setup') {
    if (hasValidAction.value) changeSection('content');
    return;
  }
  if (!isValid.value || props.isLoading) return;
  const interactive = buildInteractiveAttributes();

  if (isTemplatePurpose.value) {
    emit('save', {
      id: editingTemplateId.value,
      name: templateName.value.trim(),
      content: form.body.trim(),
      interactive,
    });
    return;
  }

  emit('send', {
    message: form.body.trim(),
    private: false,
    contentType: 'whatsapp_interactive',
    contentAttributes: { interactive },
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="5xl"
    position="top"
    overflow-y-auto
    sticky-header-footer
    :close-on-click-outside="false"
    :title="dialogTitle"
    :description="dialogDescription"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="
      activeSection === 'setup' ? !hasValidAction : !isValid
    "
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <template #header>
      <h3 class="m-0 text-base font-medium text-n-slate-12">
        {{ dialogTitle }}
      </h3>
      <p class="m-0 text-sm text-n-slate-11">{{ dialogDescription }}</p>
      <nav
        class="mb-4 flex gap-2 overflow-x-auto border-b border-n-weak pb-3"
        :aria-label="dialogTitle"
      >
        <button
          v-for="(section, index) in editorSections"
          :key="section.key"
          type="button"
          class="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
          :class="
            activeSection === section.key
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :aria-current="activeSection === section.key ? 'step' : undefined"
          @click="changeSection(section.key)"
        >
          <span
            class="grid size-5 place-items-center rounded-full border border-current text-xs"
          >
            {{ index + 1 }}
          </span>
          <span>{{ section.label }}</span>
        </button>
      </nav>
    </template>
    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <NextButton
          v-if="activeSection === 'content'"
          :label="t('PIX_KEYS.PAYMENT.BACK')"
          variant="ghost"
          color="slate"
          type="button"
          :disabled="isLoading"
          @click="changeSection('setup')"
        />
        <NextButton
          :label="t('PIX_KEYS.CANCEL')"
          variant="faded"
          color="slate"
          type="button"
          :disabled="isLoading"
          @click="close"
        />
        <NextButton
          :label="
            activeSection === 'setup'
              ? t('PIX_KEYS.PAYMENT.NEXT')
              : confirmLabel
          "
          :disabled="
            isLoading ||
            (activeSection === 'setup' ? !hasValidAction : !isValid)
          "
          :is-loading="isLoading"
          type="submit"
        />
      </div>
    </template>
    <div
      ref="editorBody"
      class="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]"
    >
      <div class="flex min-w-0 flex-col gap-5">
        <AiTemplateDraft
          v-if="isTemplatePurpose"
          :key="aiGenerationSession"
          modal
          :generate="generateAiDraft"
          :context="{
            type: form.type,
            language: 'pt_BR',
            header_format: form.headerType,
            existing_draft: aiExistingDraft,
          }"
          :disabled="isLoading"
          @apply="applyAiDraft"
        />
        <section
          v-if="isTemplatePurpose"
          v-show="activeSection === 'setup'"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <NextInput
            v-model="templateName"
            :label="t('CONVERSATION.INTERACTIVE.TEMPLATES.NAME')"
            :placeholder="
              t('CONVERSATION.INTERACTIVE.TEMPLATES.NAME_PLACEHOLDER')
            "
            maxlength="100"
          />
        </section>

        <section
          v-else-if="savedTemplates.length || isLoadingTemplates"
          v-show="activeSection === 'setup'"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.USE_TEMPLATE') }}
            <NextSelect
              v-model="selectedTemplateId"
              :options="savedTemplateOptions"
              :disabled="isLoadingTemplates"
              class="w-full [&>select]:w-full"
            />
          </label>
          <p class="mb-0 mt-2 text-xs text-n-slate-11">
            {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.USE_TEMPLATE_HELP') }}
          </p>
        </section>

        <section
          v-show="activeSection === 'setup'"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <div class="mb-4 flex items-start gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-mouse-pointer-click size-4" />
            </span>
            <div>
              <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{ t('CONVERSATION.INTERACTIVE.ACTION_TITLE') }}
              </h4>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('CONVERSATION.INTERACTIVE.ACTION_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION.INTERACTIVE.TYPE') }}
            </span>
            <div
              class="grid gap-2 sm:grid-cols-2"
              role="radiogroup"
              :aria-label="t('CONVERSATION.INTERACTIVE.TYPE')"
            >
              <button
                v-for="option in typeOptions"
                :key="option.value"
                type="button"
                role="radio"
                :data-interactive-type="option.value"
                :aria-checked="form.type === option.value"
                class="flex items-start gap-2.5 rounded-lg border p-3 text-left transition"
                :class="
                  form.type === option.value
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak hover:bg-n-alpha-1'
                "
                @click="form.type = option.value"
              >
                <span
                  :class="TYPE_ICONS[option.value]"
                  class="mt-0.5 size-4 shrink-0"
                  :style="form.type === option.value ? '' : 'opacity: 0.7'"
                  aria-hidden="true"
                />
                <span class="min-w-0">
                  <span class="block text-sm font-medium text-n-slate-12">
                    {{ option.label }}
                  </span>
                  <span class="block text-xs text-n-slate-11">
                    {{ option.hint }}
                  </span>
                </span>
              </button>
            </div>
            <p class="m-0 text-xs text-n-slate-10">
              {{ t('CONVERSATION.INTERACTIVE.TYPES_NOTE') }}
            </p>
          </div>
        </section>

        <section
          v-show="activeSection === 'content'"
          class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.INTERACTIVE.CONTENT_TITLE') }}
          </h4>
          <label
            v-if="form.type !== 'voice_call'"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.HEADER') }}
            <NextSelect
              v-model="form.headerType"
              :options="headerOptions"
              class="w-full [&>select]:w-full"
            />
          </label>
          <div
            v-if="form.type !== 'voice_call' && form.headerType === 'text'"
            ref="headerTextField"
            class="relative"
          >
            <SystemParameterMenu
              class="!absolute right-0 top-0 z-10"
              @insert="
                token => insertParameter('headerText', token, headerTextField)
              "
            />
            <NextInput
              v-model="form.headerText"
              :label="t('CONVERSATION.INTERACTIVE.HEADER_TEXT')"
              maxlength="60"
            />
          </div>
          <template v-else-if="form.type !== 'voice_call' && form.headerType">
            <NextInput
              v-model="form.headerMediaUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.MEDIA_URL')"
              placeholder="https://"
            />
            <NextInput
              v-if="form.headerType === 'document'"
              v-model="form.headerFilename"
              :label="t('CONVERSATION.INTERACTIVE.FILE_NAME')"
            />
          </template>
          <div ref="bodyField" class="relative">
            <SystemParameterMenu
              class="!absolute right-0 top-0 z-10"
              @insert="token => insertParameter('body', token, bodyField)"
            />
            <NextTextArea
              v-model="form.body"
              :label="t('CONVERSATION.INTERACTIVE.BODY')"
              :placeholder="t('CONVERSATION.INTERACTIVE.BODY_PLACEHOLDER')"
              :max-length="1024"
              show-character-count
              resize
            />
          </div>
          <div
            v-if="form.type !== 'voice_call'"
            ref="footerField"
            class="relative"
          >
            <SystemParameterMenu
              class="!absolute right-0 top-0 z-10"
              @insert="
                token => insertParameter('footerText', token, footerField)
              "
            />
            <NextInput
              v-model="form.footerText"
              :label="t('CONVERSATION.INTERACTIVE.FOOTER')"
              :placeholder="t('CONVERSATION.INTERACTIVE.FOOTER_PLACEHOLDER')"
              maxlength="60"
            />
          </div>
        </section>

        <section
          v-show="activeSection === 'setup'"
          class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          data-testid="interactive-action-configuration"
        >
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.INTERACTIVE.CONFIGURATION_TITLE') }}
          </h4>

          <template v-if="form.type === 'order_details'">
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                isTemplatePurpose
                  ? t('CONVERSATION.INTERACTIVE.PIX.PRESET_HELP')
                  : t('PIX_KEYS.PAYMENT.HELP')
              }}
            </p>
            <CurrencyInput
              v-if="!isTemplatePurpose"
              v-model="form.pixAmount"
              :label="t('CONVERSATION.INTERACTIVE.PIX.AMOUNT')"
              :disabled="isLoading"
            />
            <SavedPixKeyCard
              v-if="!isTemplatePurpose && conversationId"
              :conversation-id="conversationId"
              :amount="form.pixAmount"
              :description="form.pixDescription"
              :disabled="isLoading"
              @generated="applySavedKeyPayment"
              @invalidated="invalidateSavedKeyPayment"
            />
            <p
              v-if="pixDeliveryMode !== 'native'"
              class="m-0 text-xs text-n-slate-11"
              role="status"
            >
              {{ t('PIX_KEYS.PAYMENT.UNSUPPORTED_NOTICE') }}
            </p>
            <NextInput
              v-model="form.pixDescription"
              :label="t('CONVERSATION.INTERACTIVE.PIX.DESCRIPTION')"
              maxlength="120"
            />
            <AsaasPixChargeCard
              v-if="!isTemplatePurpose && conversationId"
              :conversation-id="conversationId"
              :amount="form.pixAmount"
              :description="form.pixDescription"
              :disabled="isLoading"
              @generated="applyAsaasCharge"
            />
            <details class="grid gap-3" :open="isTemplatePurpose">
              <summary class="cursor-pointer text-sm text-n-slate-11">
                {{ t('PIX_KEYS.PAYMENT.DETAILS') }}
              </summary>
              <div class="mt-3 grid gap-3">
                <NextInput
                  v-if="!isTemplatePurpose"
                  v-model="form.referenceId"
                  :label="t('CONVERSATION.INTERACTIVE.PIX.REFERENCE')"
                  maxlength="35"
                />
                <NextInput
                  v-model="form.merchantName"
                  :label="t('CONVERSATION.INTERACTIVE.PIX.MERCHANT')"
                  maxlength="100"
                />
                <label
                  class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                >
                  {{ t('CONVERSATION.INTERACTIVE.PIX.KEY_TYPE') }}
                  <NextSelect
                    v-model="form.pixKeyType"
                    :options="pixKeyOptions"
                  />
                </label>
                <NextInput
                  v-model="form.pixKey"
                  :label="t('CONVERSATION.INTERACTIVE.PIX.KEY')"
                  maxlength="254"
                />
                <NextInput
                  v-if="!isTemplatePurpose"
                  v-model="form.pixUrl"
                  type="url"
                  :label="t('CONVERSATION.INTERACTIVE.PIX.URL')"
                  placeholder="https://"
                  maxlength="2048"
                />
                <NextTextArea
                  v-if="!isTemplatePurpose"
                  v-model="form.pixCode"
                  :label="t('CONVERSATION.INTERACTIVE.PIX.CODE')"
                  :max-length="2048"
                  :message="
                    form.pixCode && !validPixCode(form.pixCode.trim())
                      ? t('CONVERSATION.INTERACTIVE.PIX.INVALID_CODE')
                      : ''
                  "
                  message-type="error"
                  resize
                />
              </div>
            </details>
          </template>
          <template v-else-if="form.type === 'button'">
            <div class="flex flex-col gap-2">
              <div
                v-for="(button, index) in form.buttons"
                :key="index"
                class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
              >
                <NextInput
                  v-model="button.title"
                  :label="
                    t('CONVERSATION.INTERACTIVE.BUTTON_TITLE', {
                      number: index + 1,
                    })
                  "
                  maxlength="20"
                />
                <NextButton
                  v-if="form.buttons.length > 1"
                  type="button"
                  color="ruby"
                  variant="ghost"
                  icon="i-lucide-trash-2"
                  :aria-label="t('CONVERSATION.INTERACTIVE.REMOVE_BUTTON')"
                  @click="removeButton(index)"
                />
              </div>
              <NextButton
                v-if="form.buttons.length < 3"
                type="button"
                class="self-start"
                size="sm"
                variant="faded"
                icon="i-lucide-plus"
                :label="t('CONVERSATION.INTERACTIVE.ADD_BUTTON')"
                @click="addButton"
              />
            </div>
          </template>

          <template v-else-if="form.type === 'voice_call'">
            <NextInput
              v-model="form.voiceCallText"
              :label="t('CONVERSATION.INTERACTIVE.VOICE_CALL.TEXT')"
              maxlength="20"
            />
            <NextInput
              v-model="form.voiceCallTtlMinutes"
              type="number"
              min="1"
              max="43200"
              :label="t('CONVERSATION.INTERACTIVE.VOICE_CALL.TTL')"
            />
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('CONVERSATION.INTERACTIVE.VOICE_CALL.HINT') }}
            </p>
          </template>

          <template v-else-if="form.type === 'cta_url'">
            <NextInput
              v-model="form.ctaDisplayText"
              :label="t('CONVERSATION.INTERACTIVE.LINK.TEXT')"
              maxlength="20"
            />
            <NextInput
              v-model="form.ctaUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.LINK.URL')"
              placeholder="https://"
            />
          </template>

          <div
            v-else-if="form.type === 'location_request_message'"
            class="flex items-start gap-3 rounded-lg bg-n-teal-3 p-3 text-n-teal-12"
          >
            <span class="i-lucide-map-pin size-5 shrink-0" />
            <p class="mb-0 text-sm">
              {{ t('CONVERSATION.INTERACTIVE.LOCATION_DESCRIPTION') }}
            </p>
          </div>

          <template v-else-if="form.type === 'flow'">
            <div
              class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
              data-testid="interactive-flow-configuration"
            >
              <div class="flex flex-wrap items-end gap-2">
                <label
                  class="flex min-w-0 flex-1 flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                >
                  {{ t('CONVERSATION.INTERACTIVE.FLOW.SELECT') }}
                  <ComboBox
                    :model-value="form.flowId"
                    :options="flowOptions"
                    :display-label="selectedFlow?.name || form.flowId"
                    :placeholder="
                      t('CONVERSATION.INTERACTIVE.FLOW.SELECT_PLACEHOLDER')
                    "
                    :search-placeholder="
                      t('CONVERSATION.INTERACTIVE.FLOW.SEARCH')
                    "
                    :empty-state="t('CONVERSATION.INTERACTIVE.FLOW.EMPTY')"
                    :disabled="isLoadingFlows"
                    class="w-full"
                    @update:model-value="selectFlow"
                  />
                </label>
                <NextButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  icon="i-hugeicons-refresh"
                  :is-loading="isLoadingFlows"
                  :aria-label="t('CONVERSATION.INTERACTIVE.FLOW.REFRESH')"
                  :title="t('CONVERSATION.INTERACTIVE.FLOW.REFRESH')"
                  @click="loadWhatsappFlows"
                />
                <NextButton
                  type="button"
                  size="sm"
                  variant="faded"
                  icon="i-hugeicons-add-01"
                  :label="t('CONVERSATION.INTERACTIVE.FLOW.CREATE')"
                  @click="openFlowCreation"
                />
              </div>
              <p class="mb-0 text-xs text-n-slate-11">
                {{
                  flowsLoaded && !selectableFlows.length
                    ? t('CONVERSATION.INTERACTIVE.FLOW.EMPTY_HELP')
                    : t('CONVERSATION.INTERACTIVE.FLOW.SELECT_HELP')
                }}
              </p>
              <p
                v-if="form.flowMode === 'draft'"
                class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-2 p-2 text-xs text-n-amber-12"
              >
                <span class="i-hugeicons-alert-circle size-4 shrink-0" />
                {{ t('CONVERSATION.INTERACTIVE.FLOW.DRAFT_HINT') }}
              </p>
              <p
                v-if="flowOutsideInbox"
                class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-2 p-2 text-xs text-n-amber-12"
              >
                <span class="i-hugeicons-alert-circle size-4 shrink-0" />
                {{ t('CONVERSATION.INTERACTIVE.FLOW.NOT_IN_INBOX') }}
              </p>
            </div>
            <NextInput
              v-model="form.flowCta"
              :label="t('CONVERSATION.INTERACTIVE.FLOW.CTA')"
              :placeholder="t('CONVERSATION.INTERACTIVE.FLOW.CTA_PLACEHOLDER')"
              maxlength="20"
            />
            <details class="rounded-xl border border-n-weak p-3">
              <summary
                class="cursor-pointer text-sm font-medium text-n-slate-12"
              >
                {{ t('CONVERSATION.INTERACTIVE.FLOW.ADVANCED') }}
              </summary>
              <div class="mt-3 flex flex-col gap-4">
                <div class="grid gap-4 sm:grid-cols-2">
                  <NextInput
                    v-model="form.flowId"
                    :label="t('CONVERSATION.INTERACTIVE.FLOW.ID')"
                    :message="t('CONVERSATION.INTERACTIVE.FLOW.MANUAL_ID_HELP')"
                  />
                  <NextInput
                    v-model="form.flowToken"
                    :label="t('CONVERSATION.INTERACTIVE.FLOW.TOKEN')"
                    :message="t('CONVERSATION.INTERACTIVE.FLOW.TOKEN_HELP')"
                  />
                  <label
                    class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CONVERSATION.INTERACTIVE.FLOW.ACTION') }}
                    <NextSelect
                      v-model="form.flowAction"
                      :options="flowActionOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <label
                    class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CONVERSATION.INTERACTIVE.FLOW.MODE') }}
                    <NextSelect
                      v-model="form.flowMode"
                      :options="flowModeOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <NextInput
                    v-if="form.flowAction === 'navigate'"
                    v-model="form.flowScreen"
                    :label="t('CONVERSATION.INTERACTIVE.FLOW.SCREEN')"
                    :message="
                      form.flowScreen.trim()
                        ? ''
                        : t('CONVERSATION.INTERACTIVE.FLOW.SCREEN_REQUIRED')
                    "
                    :message-type="form.flowScreen.trim() ? 'info' : 'error'"
                  />
                </div>
                <NextTextArea
                  v-model="form.flowData"
                  :label="t('CONVERSATION.INTERACTIVE.FLOW.DATA')"
                  :placeholder="
                    t('CONVERSATION.INTERACTIVE.FLOW.DATA_PLACEHOLDER')
                  "
                  :message="flowDataError"
                  :message-type="flowDataError ? 'error' : 'info'"
                  resize
                />
              </div>
            </details>
          </template>

          <template v-else-if="form.type === 'catalog_message'">
            <NextInput
              v-model="form.catalogThumbnailProductRetailerId"
              :label="t('CONVERSATION.INTERACTIVE.CATALOG.PRODUCT_ID')"
              :message="t('CONVERSATION.INTERACTIVE.CATALOG.PRODUCT_HELP')"
            />
          </template>
        </section>
      </div>

      <aside class="lg:sticky lg:top-0 lg:self-start">
        <p
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('CONVERSATION.INTERACTIVE.PREVIEW.TITLE') }}
        </p>
        <div class="rounded-2xl bg-[#efeae2] p-4 dark:bg-n-solid-2">
          <div
            class="overflow-hidden rounded-xl bg-white text-[#334155] shadow-sm"
          >
            <div
              v-if="form.headerType"
              class="flex min-h-16 items-center justify-center bg-[#f1f5f9] px-4 py-3 text-sm font-semibold"
            >
              <span v-if="form.headerType === 'text'">
                {{
                  form.headerText ||
                  t('CONVERSATION.INTERACTIVE.PREVIEW.HEADER')
                }}
              </span>
              <span v-else class="flex items-center gap-2 text-[#64748b]">
                <span class="i-lucide-paperclip size-4" />
                {{
                  headerOptions.find(option => option.value === form.headerType)
                    ?.label
                }}
              </span>
            </div>
            <div class="px-4 py-3">
              <p class="mb-0 whitespace-pre-wrap text-sm">
                {{ form.body || t('CONVERSATION.INTERACTIVE.PREVIEW.BODY') }}
              </p>
              <p
                v-if="form.footerText"
                class="mb-0 mt-2 text-xs text-[#64748b]"
              >
                {{ form.footerText }}
              </p>
            </div>
            <div
              v-if="form.type === 'order_details'"
              class="border-t border-[#e2e8f0] px-4 py-3"
            >
              <p class="mb-1 text-xs">{{ form.pixDescription }}</p>
              <strong class="text-lg">{{
                isTemplatePurpose
                  ? t('CONVERSATION.INTERACTIVE.PIX.AMOUNT_ON_SEND')
                  : new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(Number(form.pixAmount) || 0)
              }}</strong>
              <p class="mb-0 mt-1 text-xs">{{ form.merchantName }}</p>
            </div>
            <div
              v-if="form.type === 'button'"
              class="border-t border-[#e2e8f0]"
            >
              <div
                v-for="(button, index) in form.buttons"
                :key="`${button.title}-${index}`"
                class="flex min-h-10 items-center justify-center border-b border-[#e2e8f0] px-3 text-sm font-medium text-[#0284c7] last:border-b-0"
              >
                {{
                  button.title || t('CONVERSATION.INTERACTIVE.PREVIEW.BUTTON')
                }}
              </div>
            </div>
            <div
              v-else
              class="flex min-h-11 items-center justify-center gap-2 border-t border-[#e2e8f0] px-3 text-sm font-medium text-[#0284c7]"
            >
              <span
                :class="{
                  'i-hugeicons-money-receive-01': form.type === 'order_details',
                  'i-lucide-external-link': form.type === 'cta_url',
                  'i-lucide-phone-call': form.type === 'voice_call',
                  'i-lucide-map-pin': form.type === 'location_request_message',
                  'i-lucide-list-checks': form.type === 'flow',
                  'i-lucide-shopping-bag': form.type === 'catalog_message',
                }"
                class="size-4"
              />
              {{ actionLabel || t('CONVERSATION.INTERACTIVE.PREVIEW.ACTION') }}
            </div>
          </div>
        </div>
        <p class="mb-0 mt-3 text-xs text-n-slate-11">
          {{ t('CONVERSATION.INTERACTIVE.WINDOW_NOTICE') }}
        </p>
      </aside>
    </div>
  </Dialog>
</template>
